import { useCallback, useEffect, useRef } from "react";

// Briefly highlight the row matching `focusItemId` once it mounts. Returns a
// `register(id)(el)` callback to spread onto each row's ref. Lead activity
// timeline rows use this to land the user on the call/note/contact they just
// clicked in the Activity tab.
//
// - Scrolls the matched row into view (block: "center") on next paint.
// - Adds a transient outline + soft tint via the `data-crm-focus="on"`
//   attribute; the panel sets up the CSS once. Flash lasts ~1500ms.
// - Calls `onResolved` once after the flash so the parent can clear the
//   focused id (prevents re-flashing if the same row re-renders).
export default function useFocusRow(focusItemId, { onResolved } = {}) {
  const refs = useRef(new Map());
  const flashedRef = useRef(null);

  const register = useCallback((id) => (el) => {
    if (!id) return;
    if (el) refs.current.set(String(id), el);
    else refs.current.delete(String(id));
  }, []);

  useEffect(() => {
    if (!focusItemId) return;
    const id = String(focusItemId);
    if (flashedRef.current === id) return;
    const el = refs.current.get(id);
    if (!el) return;
    flashedRef.current = id;
    const raf = requestAnimationFrame(() => {
      try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (_) {}
      el.setAttribute("data-crm-focus", "on");
    });
    const t = setTimeout(() => {
      el.removeAttribute("data-crm-focus");
      if (onResolved) onResolved();
    }, 1500);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [focusItemId, onResolved]);

  return { register };
}

// CSS to inject once per panel that uses focus rows. The selector targets the
// transient attribute so we don't fight existing row styling.
export const FOCUS_ROW_STYLE = `
  [data-crm-focus="on"] {
    background: var(--crm-primary-soft) !important;
    box-shadow: inset 0 0 0 1px var(--crm-primary);
    border-radius: 8px;
    transition: background 200ms ease, box-shadow 200ms ease;
  }
`;
